import Vue from 'vue'
import axios from 'axios'
import BasketSource from '@/models/enums/BasketSource'
import { nanoid } from 'nanoid'
import _ from 'lodash'
import OrderStatus from '@/models/enums/OrderStatus'

const updateErrorMessage = {
  status: 422,
  response: {
    data: {
      message: 'Редактирование заказа запрещено',
    },
  },
}

const changingPaidOrderError = {
  status: 422,
  response: {
    data: {
      message: 'Изменение стоимости оплаченного заказа запрещено',
    },
  },
}

const deliveryChangeError = {
  status: 422,
  response: {
    data: {
      message: 'Смена доставки недоступна - заказ уже в пути или завершён',
    },
  },
}

const filialChangeError = {
  status: 422,
  response: {
    data: {
      message: 'Смена филиала недоступна',
    },
  },
}

// state.orderStatus приходит в camelCase, как в OrderStatus (не приводить к lower case)
const orderStatusIn = (statuses) => Boolean(state.orderStatus) && statuses.includes(state.orderStatus)
const isItemsLocked = () => orderStatusIn(OrderStatus.prohibitingItemsEditingStatuses)
const isDeliveryLocked = () => orderStatusIn(OrderStatus.prohibitingDeliveryChangeStatuses)
const isOrderClosed = () => orderStatusIn(OrderStatus.closedStatuses)

// Фактическая смена: цель отличается от текущего филиала корзины
const isFilialLocked = (targetFilialId, isOrderPaid) => {
  if (!targetFilialId || Number(targetFilialId) === Number(state.basket?.filialId)) {
    return false
  }
  return isOrderPaid || orderStatusIn(OrderStatus.prohibitingFilialChangeStatuses)
}

const state = {
  isBasketLoading: false,
  basket: {},
  basketItems: [],
  orderStatus: null,
  total: 0,
  drinkSaleText: 'Хотите добавим вам напиток к заказу?',
}

const getGroupedBasketItems = (basketItems) => {
  const idList = []
  const result = []

  basketItems.forEach((item) => {
    const id = item.menuItemId + '-' + item.modificationMenuItemId

    const existItem = idList[id]
    if (existItem) {
      const quantity = result[existItem.key].quantity + (item?.serviceFeeZeroed ? 0 : 1)
      const price = result[existItem.key].price + item.price
      const comment = item.comment ? item.comment : result[existItem.key].comment
      const free = item.free ? item.free : result[existItem.key].free
      const sort = item.sort ? item.sort : result[existItem.key].sort

      result[existItem.key] = {
        ...result[existItem.key],
        quantity,
        price,
        comment,
        free,
        sort,
      }
    } else {
      const hash = nanoid()
      idList[id] = {
        key: result.length,
        hash: hash,
      }
      result.push({
        ...item,
        quantity: item?.serviceFeeZeroed ? 0 : 1,
        hash: hash,
      })
    }
  })

  const sorted = _.sortBy(result, ['sort', 'free', 'serviceFee'])
  return sorted
}

const getNormalBasketItems = (basketItems) => {
  const usualItems = []
  basketItems.forEach((item) => {
    if (!item.modificationMenuItem && !item.comment) {
      usualItems.push(item)
    }
  })
  let usualItemsIds = usualItems.map((item) => item.menuItem.id)
  usualItemsIds = [...new Set(usualItemsIds)]
  const aggregatedItems = {}
  usualItemsIds.forEach((id) => {
    const item = usualItems.find((item) => item.menuItem.id === id)
    const quantity = usualItems.filter((item) => item.menuItem.id === id)
    aggregatedItems[id] = {
      ...item,
      quantity: quantity.length,
    }
  })
  return aggregatedItems
}

const getModifiedBasketItems = (basketItems) => {
  const usualItems = []
  basketItems.forEach((item) => {
    if (item.modificationMenuItem && !item.comment) {
      usualItems.push(item)
    }
  })
  let usualItemsIds = usualItems.map((item) => item.menuItem.id)
  usualItemsIds = [...new Set(usualItemsIds)]
  const aggregatedItems = {}
  usualItemsIds.forEach((id) => {
    const item = usualItems.find((item) => item.menuItem.id === id)
    const quantity = usualItems.filter((item) => item.menuItem.id === id)
    aggregatedItems[id] = {
      ...item,
      quantity: quantity.length,
    }
  })
  return aggregatedItems
}

const getCommentedBasketItems = (basketItems) => {
  const usualItems = []
  basketItems.forEach((item) => {
    if (item.comment) {
      usualItems.push(item)
    }
  })
  let usualItemsIds = usualItems.map((item) => item.menuItem.id)
  usualItemsIds = [...new Set(usualItemsIds)]
  const aggregatedItems = {}
  usualItemsIds.forEach((id) => {
    const item = usualItems.find((item) => item.menuItem.id === id)
    const quantity = usualItems.filter((item) => item.menuItem.id === id)
    aggregatedItems[id] = {
      ...item,
      quantity: quantity.length,
    }
  })
  return aggregatedItems
}

const actions = {
  fetchSingleBasket ({ commit, dispatch, rootGetters }, id) {
    commit('setBasketLoading', true)
    return axios
      .get(rootGetters['api/basketsSingle'](id))
      .then((res) => {
        commit('setBasket', res.data.data)
        return res
      })
      .catch((err) => err)
      .finally(() => {
        commit('setBasketLoading', false)
      })
  },
  fetchSingleBasketByClient ({ commit, dispatch, rootGetters }, id) {
    commit('setBasketLoading', true)
    return axios
      .get(rootGetters['api/basketsSingleByClient'](id))
      .then((res) => {
        commit('setBasket', res.data.data)
        return res
      })
      .catch((err) => err)
      .finally(() => {
        commit('setBasketLoading', false)
      })
  },
  createBasket ({ rootGetters }, data) {
    return axios
      .post(rootGetters['api/basketsCreate'], data)
      .then((res) => {
        return res
      })
      .catch((err) => err)
  },
  addBasketItem ({ commit, rootGetters }, { data, id }) {
    if (rootGetters['orders/isOrderPaid']) {
      return changingPaidOrderError
    }

    if (isItemsLocked()) {
      return updateErrorMessage
    }

    commit('setBasketLoading', true)
    return axios
      .post(rootGetters['api/basketsAddItem'](id), data)
      .then((res) => {
        return res
      })
      .catch((err) => err)
      .finally(() => {
        commit('setBasketLoading', false)
      })
  },
  updateBasketItem ({ commit, rootGetters }, { data, id }) {
    if (rootGetters['orders/isOrderPaid']) {
      return changingPaidOrderError
    }

    if (isItemsLocked()) {
      return updateErrorMessage
    }

    commit('setBasketLoading', true)
    return axios.patch(rootGetters['api/basketsUpdateItem'](id), data).then(res => {
        return res
      }).catch(err => err).finally(() => {
        commit('setBasketLoading', false)
      })
  },
  deleteBasketItem ({ commit, rootGetters }, { data, id }) {
    if (rootGetters['orders/isOrderPaid']) {
      return changingPaidOrderError
    }

    if (isItemsLocked()) {
      return updateErrorMessage
    }

    commit('setBasketLoading', true)
    return axios
      .delete(rootGetters['api/basketsDeleteItem'](id), data)
      .then((res) => {
        return res
      })
      .catch((err) => err)
      .finally(() => {
        commit('setBasketLoading', false)
      })
  },
  updateBasketPayment ({ commit, rootGetters }, { data, id }) {
    if (rootGetters['orders/isOrderPaid']) {
      return changingPaidOrderError
    }

    if (state.orderStatus && [OrderStatus.Completed, OrderStatus.Canceled].includes(state.orderStatus.toLowerCase())) {
      return updateErrorMessage
    }

    commit('setBasketLoading', true)
    return axios
      .patch(rootGetters['api/basketsUpdatePayment'](id), data)
      .then((res) => {
        return res
      })
      .catch((err) => err)
      .finally(() => {
        commit('setBasketLoading', false)
      })
  },
  changeOnYandexDelivery ({ rootGetters }, id) {
    if (isDeliveryLocked()) {
      return deliveryChangeError
    }

    return axios
      .patch(rootGetters['api/basketChangeOnYandexDelivery'](id))
      .then((res) => {
        return res
      })
      .catch((err) => err)
  },
  changeOnDeliveryClub ({ rootGetters }, id) {
    if (isDeliveryLocked()) {
      return deliveryChangeError
    }

    return axios
      .patch(rootGetters['api/basketChangeOnDeliveryClub'](id))
      .then((res) => {
        return res
      })
      .catch((err) => err)
  },
  updateBasketAddress ({ rootGetters }, { data, id }) {
    if (isDeliveryLocked()) {
      return deliveryChangeError
    }

    return axios
      .patch(rootGetters['api/basketsUpdateAddress'](id), data)
      .then((res) => {
        return res
      })
      .catch((err) => err)
  },
  changeDeliveryToPickup ({ rootGetters }, { data, id }) {
    if (isDeliveryLocked()) {
      return deliveryChangeError
    }

    if (isFilialLocked(data.filialId, rootGetters['orders/isOrderPaid'])) {
      return filialChangeError
    }

    return axios
      .patch(rootGetters['api/basketsChangePickup'](id), data)
      .then((res) => {
        return res
      })
      .catch((err) => err)
  },
  updateBasket ({ commit, rootGetters }, { data, id }) {
    if (isOrderClosed()) {
      return updateErrorMessage
    }

    if (rootGetters['orders/isOrderPaid'] && ('promoCodeId' in data || 'promoCode' in data)) {
      return changingPaidOrderError
    }

    if ('filialId' in data && isFilialLocked(data.filialId, rootGetters['orders/isOrderPaid'])) {
      return filialChangeError
    }

    return axios.patch(rootGetters['api/basketsUpdateInfo'](id), data).then(res => {
      if (res.data.data) {
        commit('setBasket', res.data.data)
      }
      return res
    }).catch((err) => err)
  },

   updateCommentForCourier ({ dispatch, commit, rootGetters }, { value, id }) {
     if (isOrderClosed()) {
       return Promise.reject(new Error('Редактирование комментария возможно только у незавершенных заказов'))
     }
     const data = {
       commentForCourier: value,
     }
     return axios.patch(rootGetters['api/basketsUpdateInfo'](id), data).then(res => {
       if (res.data.data) {
         commit('setBasket', res.data.data)
       }
       return res.data.data
     })
  },
  updateComments ({ commit, rootGetters }, { data, id }) {
    if (isOrderClosed()) {
      return updateErrorMessage
    }

    commit('setBasketLoading', true)
    return axios.patch(rootGetters['api/basketsUpdateComments'](id), data).then(res => {
      if (res.data.data) {
        commit('setBasket', res.data.data)
      }
      return res
    })
      .catch((err) => err)
      .finally(() => {
        commit('setBasketLoading', false)
      })
  },

  updateClientRegistrationSource ({ commit, rootGetters }, { data, id }) {
    // commit('setBasketLoading', true)
    return axios.patch(rootGetters['api/basketsUpdateClientRegistrationSource'](id), data)
      .then(res => {
        if (res.data.data) {
          commit('setBasket', res.data.data)
        }
        return res.data.data
      })
      .finally(() => {
        // commit('setBasketLoading', false)
      })
  },
}

const mutations = {
  setBasket (state, payload) {
    state.basket = payload
  },
  setBasketLoading (state, value) {
    state.isBasketLoading = value
  },
  // Количество персон до ответа сервера: счётчик шлёт запрос с задержкой,
  // а проверки степпера читают persons из стора
  setBasketPersons (state, persons) {
    // Поле на месте, а не новый объект: вотчеры на basket целиком не должны дёргаться
    Vue.set(state.basket, 'persons', persons)
  },
  setOrderStatus (state, value) {
    state.orderStatus = value
  },
}

const getters = {
  getItems: (state) => {
    return state.basket.items ? state.basket.items : []
  },
  getTotalPrice: (state) => {
    return state.basket.totalPrice || 0
  },
  getNormalBasketItems: (state) => {
    if (!state.basket.items) {
      return []
    } else {
      return getGroupedBasketItems(state.basket.items)
      // return getNormalBasketItems(state.basket.items)
    }
  },
  getModifiedBasketItems: (state) => {
    return []
    /* if (!state.basket.items) {
      return null
    } else {
      return getModifiedBasketItems(state.basket.items)
    } */
  },
  getCommentedBasketItems: (state) => {
    return []
    /* if (!state.basket.items) {
      return null
    } else {
      return getCommentedBasketItems(state.basket.items)
    } */
  },
  basketDeliveryValid: (state) => {
    if (state.basket && state.basket.deliveryType) {
      return true
    }
    return false
  },

  isSourceYandex: (state) => {
    return state.basket && state.basket.basketSource === BasketSource.Yandex
  },
  isSourceDeliveryClub: (state) => {
    return state.basket && state.basket.basketSource === BasketSource.DeliveryClub
  },
  isSourceAggregator: (state) => {
    return state.basket?.isSourceAggregator
  },

  isFilialLocked: (state, getters, rootState, rootGetters) => (targetFilialId) =>
    isFilialLocked(targetFilialId, rootGetters['orders/isOrderPaid']),
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
}
